import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CartService } from '../_services/cart.service';
import { DeliveryService } from '../_services/delivery.service';
import { UserService } from '../_services/user.service';
import { CountryService } from '../country/country.service';
import { ShippingMeanService } from '../_services/shipping-mean.service';
import { Country } from '../country/Country';
import { ProductModel } from '../_cart/product-model';
import { ShippingPricingItem } from '../_models/shipping-pricing-response';
import { User } from '../_models/user';
import { environment } from '../../environments/environment';

@Component({
    selector: 'app-delivery',
    templateUrl: './delivery.component.html',
    styleUrls: ['./delivery.component.css'],
    standalone: false
})
export class DeliveryComponent implements OnInit, OnDestroy {
    private readonly cartService = inject(CartService);
    private readonly deliveryService = inject(DeliveryService);
    private readonly userService = inject(UserService);
    private readonly countryService = inject(CountryService);
    private readonly shippingMeanService = inject(ShippingMeanService);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);
    private readonly toastr = inject(ToastrService);
    private readonly fb = inject(UntypedFormBuilder);

    readonly currency = environment.currency;

    user: User | null = null;
    products: ProductModel[] = [];
    shippingPricingsMap = new Map<number, ShippingPricingItem[]>();
    loading = true;
    loadingPricing = false;

    // Shipping address properties
    shippingAddresses: any[] = [];
    primaryAddress: any = null;
    addressLoading = true;
    showAddressForm = false;
    addressForm: UntypedFormGroup;
    countries: Country[] = [];
    cities: any[] = [];
    selectedCountry: Country | null = null;
    savingAddress = false;

    private subs: Subscription[] = [];

    ngOnInit(): void {
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? 'null');
        this.loadCountries();
        this.loadShippingAddresses();
        this.loadProducts();
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
    }

    private loadProducts(): void {
        const sub = this.cartService.getProducts().subscribe(products => {
            const selectedProducts = products.filter(p => p.selected);
            
            if (selectedProducts.length === 0) {
                if (products.length > 0) {
                    this.translateService.get('cart.select_items_to_checkout').subscribe(msg => {
                        this.toastr.warning(msg);
                        this.router.navigateByUrl('shopping-cart');
                    });
                } else {
                    this.translateService.get('cart.is_empty_message').subscribe(msg => {
                        this.toastr.warning(msg);
                        this.router.navigateByUrl('shopping-cart');
                    });
                }
                return;
            }

            this.products = selectedProducts;
            this.fetchShippingPricings();
        });
        this.subs.push(sub);
    }

    private fetchShippingPricings(): void {
        this.loadingPricing = true;
        
        const items = this.products.map(p => ({
            offerId: p.id,
            qty: p.qty
        }));

        const sub = this.deliveryService.getShippingPricing({ items }).subscribe({
            next: (response) => {
                const pricingData = response?.data?.orderItemShippingPricing || {};
                
                for (const [offerIdStr, pricings] of Object.entries(pricingData)) {
                    const offerId = parseInt(offerIdStr, 10);
                    this.shippingPricingsMap.set(offerId, pricings);
                    
                    // Update cached selection with fresh pricing data
                    const existingSelection = this.deliveryService.getSelection(offerId);
                    if (existingSelection) {
                        const freshPricing = pricings.find((p: any) => p.id === existingSelection.id);
                        if (freshPricing) {
                            this.deliveryService.setSelection(offerId, freshPricing);
                        }
                    }
                }
                
                this.loadingPricing = false;
                this.loading = false;
            },
            error: () => {
                this.toastr.error('Failed to load shipping options');
                this.loadingPricing = false;
                this.loading = false;
            }
        });
        
        this.subs.push(sub);
    }

    selectShipping(offerId: number, pricing: ShippingPricingItem): void {
        this.deliveryService.setSelection(offerId, pricing);
    }

    selectLocalPickup(offerId: number): void {
        this.deliveryService.setLocalPickup(offerId, true);
    }

    isSelected(offerId: number, pricing: ShippingPricingItem): boolean {
        const selected = this.deliveryService.getSelection(offerId);
        return selected?.id === pricing.id;
    }

    isLocalPickupSelected(offerId: number): boolean {
        return this.deliveryService.isLocalPickupSelected(offerId);
    }

    isAnySelected(offerId: number): boolean {
        return this.isSelected(offerId, {} as ShippingPricingItem) || this.isLocalPickupSelected(offerId);
    }

    hasShippingOptions(offerId: number): boolean {
        const pricings = this.shippingPricingsMap.get(offerId);
        return pricings && pricings.length > 0;
    }

    getShippingOptions(offerId: number): ShippingPricingItem[] {
        return this.shippingPricingsMap.get(offerId) || [];
    }

    canProceed(): boolean {
        if (!this.primaryAddress) {
            return false;
        }
        for (const product of this.products) {
            const pricings = this.shippingPricingsMap.get(product.id);
            const hasLocalPickup = product.localPickup;
            if ((pricings && pricings.length > 0) || hasLocalPickup) {
                if (!this.deliveryService.hasSelection(product.id)) {
                    return false;
                }
            }
        }
        return true;
    }

    goToCheckout(): void {
        if (!this.primaryAddress) {
            this.translateService.get('delivery.no_address_warning').subscribe(msg => {
                this.toastr.warning(msg);
            });
            return;
        }
        if (!this.canProceed()) {
            this.translateService.get('delivery.select_shipping').subscribe(msg => {
                this.toastr.warning(msg);
            });
            return;
        }
        this.router.navigateByUrl('checkout');
    }

    backToCart(): void {
        this.router.navigateByUrl('shopping-cart');
    }

    getImageSrc(imagePath: string): string {
        return imagePath && imagePath.includes('media')
            ? imagePath.includes('?') ? imagePath : `${imagePath}?func=crop&width=75&height=75`
            : '/assets/images/default.jpg';
    }

    getDeliveryMessage(eta: number): string {
        if (!eta || eta < 0) return '';
        
        const today = new Date();
        const deliveryDate = new Date(today);
        deliveryDate.setDate(today.getDate() + eta);
        
        const lang = this.translateService.currentLang === 'fr' ? 'fr-FR' : 'en-US';
        const dateOpts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
        const deliveryStr = deliveryDate.toLocaleDateString(lang, dateOpts);
        
        if (eta === 0) {
            return this.translateService.instant('delivery.delivered_today', { date: deliveryStr });
        }
        if (eta === 1) {
            return this.translateService.instant('delivery.delivered_tomorrow', { date: deliveryStr });
        }
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        const startStr = tomorrow.toLocaleDateString(lang, dateOpts);
        return this.translateService.instant('delivery.delivered_between', { start: startStr, end: deliveryStr });
    }

    getLogisticAgentLabel(pricing: ShippingPricingItem): string {
        return pricing.shippingOption?.logisticAgent || 
               pricing.shippingOption?.shippingType?.description || 
               'Standard';
    }

    calculateItemsTotal(): number {
        return this.products.reduce((sum, p) => sum + (p.qty * p.price), 0);
    }

    calculateShippingTotal(): number {
        let total = 0;
        for (const product of this.products) {
            if (this.deliveryService.isLocalPickupSelected(product.id)) {
                continue;
            }
            const selected = this.deliveryService.getSelection(product.id);
            if (selected) {
                total += selected.price * product.qty;
            }
        }
        return total;
    }

    calculateGrandTotal(): number {
        return this.calculateItemsTotal() + this.calculateShippingTotal();
    }

    // ==================== SHIPPING ADDRESS METHODS ====================

    loadCountries(): void {
        this.countryService.loadAll().subscribe({
            next: (data) => {
                this.countries = data;
                this.loadCities();
            },
            error: () => {
                this.countries = [];
            }
        });
    }

    loadCities(): void {
        const userCountryId = this.user?.country 
            ? (typeof this.user.country === 'object' ? this.user.country?.id : this.user.country)
            : 37;
        if (userCountryId) {
            this.shippingMeanService.getCitiesByCountry(Number(userCountryId)).subscribe({
                next: (response: any) => {
                    this.cities = response?.data || response || [];
                },
                error: () => {
                    this.cities = [];
                }
            });
        }
    }

    loadShippingAddresses(): void {
        if (!this.user?.id) return;
        
        this.addressLoading = true;
        
        this.userService.loadShippingAddresses(this.user.id).subscribe({
            next: (response: any) => {
                let addresses = response?.data || response || [];
                addresses = addresses.map((addr: any) => ({
                    ...addr,
                    countryId: addr.country?.id || addr.countryId || 37
                }));
                this.shippingAddresses = addresses;
                this.primaryAddress = addresses.find((a: any) => a.status?.toLowerCase() === 'primary') || null;
                this.addressLoading = false;
            },
            error: () => {
                this.shippingAddresses = [];
                this.primaryAddress = null;
                this.addressLoading = false;
            }
        });
    }

    addAddress(): void {
        this.generateAddressForm(null);
        this.showAddressForm = true;
    }

    editAddress(address: any): void {
        this.generateAddressForm(address);
        this.showAddressForm = true;
    }

    generateAddressForm(address: any): void {
        const defaultCountryId = address ? address.countryId : (this.user?.country 
            ? (typeof this.user.country === 'object' ? this.user.country?.id : this.user.country) 
            : 37);
        
        this.addressForm = this.fb.group({
            id: new UntypedFormControl(address ? address.id : 0),
            name: new UntypedFormControl(address ? address.name : '', Validators.required),
            region: new UntypedFormControl(address ? address.region : ''),
            status: new UntypedFormControl(address ? address.status : ''),
            town: new UntypedFormControl(address ? address.town : '', Validators.required),
            countryId: new UntypedFormControl(defaultCountryId, Validators.required),
            addressLine1: new UntypedFormControl(address ? address.addressLine1 : '', Validators.required),
            addressLine2: new UntypedFormControl(address ? address.addressLine2 : ''),
        });
        
        this.selectedCountry = this.countries.find(c => c.id === defaultCountryId) ?? null;
        if (defaultCountryId) {
            this.loadCitiesForCountry(Number(defaultCountryId));
        }
    }

    loadCitiesForCountry(countryId: number): void {
        this.shippingMeanService.getCitiesByCountry(countryId).subscribe({
            next: (response: any) => {
                this.cities = response?.data || response || [];
            },
            error: () => {
                this.cities = [];
            }
        });
    }

    onCountryChange(event: any): void {
        const id = +event.target.value;
        this.selectedCountry = this.countries.find(c => c.id === id) ?? null;
        this.addressForm.get('town')?.setValue('');
        if (id) {
            this.loadCitiesForCountry(id);
        } else {
            this.cities = [];
        }
    }

    saveAddress(): void {
        if (!this.addressForm?.valid || !this.user?.id) return;
        
        this.savingAddress = true;
        const formValue = this.addressForm.value;
        
        if (formValue.id === 0) {
            this.addressForm.removeControl('status');
        }
        
        this.userService.saveShippingAddress(this.user.id, this.addressForm.value).subscribe({
            next: () => {
                this.showAddressForm = false;
                this.savingAddress = false;
                this.loadShippingAddresses();
                this.translateService.get('delivery.address_saved').subscribe(msg => {
                    this.toastr.success(msg);
                });
            },
            error: () => {
                this.savingAddress = false;
                this.translateService.get('delivery.address_save_error').subscribe(msg => {
                    this.toastr.error(msg);
                });
            }
        });
    }

    closeAddressForm(): void {
        this.showAddressForm = false;
    }

    getCountryName(id: number): string {
        const country = this.countries.find(c => c.id === id);
        return country ? `${country.iso}` : '';
    }

    getCountryFlag(id: number): string {
        const country = this.countries.find(c => c.id === id);
        return country ? `https://flagcdn.com/16x12/${country.iso?.toLowerCase()}.png` : '';
    }

    get addressFormControls() {
        return this.addressForm?.controls;
    }
}
